import { useEffect, useState, useRef } from 'react'
import { defineMessages, useIntl } from 'react-intl'
import GoogleMapReact, { BootstrapURLKeys } from 'google-map-react'
import { LinkButton, ButtonGroup, Icon, Flex, Button } from '@homebotapp/hb-react-component-catalog'
import { useSelector, useDispatch } from 'react-redux'
import { TrackingEventConstants } from '../../../../constants/tracking'
import { useTrackingDebounceSetters } from '../../../../hooks/tracking'
import { Link as ReactRouterLink } from 'react-router-dom'
import { selectRouteToFavoriteListings, selectRouteToSavedSearches } from '../../../../store/selectors/routes'
import { HomeSearchTrackingEventLocation } from '../../../../constants/tracking'
import { toggleAuthModal } from '../../../../actions/homeSearchAuthModal'
import { MIN_MAP_ZOOM, MAX_MAP_ZOOM, STARTING_ZOOM } from '../../../../constants/listings'
import { useTheme } from '@homebotapp/hb-react-component-catalog'
import { selectSelectedListingId } from '../../../../store/selectors/listings'
import { setPreviouslyViewedListingId, setSelectedListingId } from '../../../../actions/listings'
import { selectClientIsGuest } from 'src/store/selectors/client'
import { MagnifyingGlassLoader } from '../../..'
import { Marker } from './Marker/Marker'
import useViewport from 'src/hooks/useViewport'
import { useTrackingContext } from '../../../../providers/tracking'
import styles from './ListingsMap.module.scss'
import { SavedSearchIcon } from '../SavedSearchToggle/SavedSearchIcon'
import { useGetListingsForArea } from 'src/hooks/listings/useGetListingsForArea'
import { useFilters } from '../hook/useFilters'
import { useRecoilState } from 'recoil'
import { homeSearchMapState } from './state/homeSearchMapState'
import { hasCustomerToken } from 'src/auth'
import { currentSearchLocation } from '../../GoogleAutoComplete/state/currentSearchLocation'
import { setFilter } from '../../../../actions/filters'
import { GeoAreaLevel } from '../../../../api/gqlaxy/gql/generated/graphql'
import qs from 'qs'
import { camelizeKeys } from '../../../../helpers/utils'
const REACT_APP_GOOGLE_MAPS_ID_LIGHT = 'b2cd31d8769baca7'
const REACT_APP_GOOGLE_MAPS_ID_DARK = '352ac5bc0611690c'
import { fitBounds } from 'google-map-react'

export const MSG = defineMessages({
  favorites: {
    id: 'Search.header.favorites',
    defaultMessage: 'Favorites'
  },
  searches: {
    id: 'Search.header.searches',
    defaultMessage: 'Saved Searches'
  },
  searchThisArea: {
    id: 'Search.searchThisArea',
    defaultMessage: 'Search this area'
  }
})

export const ListingsMap = ({
  filter,
  location,
  onMarkerClick,
  isDelayLoading,
  scrollBehavior,
  setIsMobileViewExpanded
}) => {
  const dispatch = useDispatch()
  const intl = useIntl()
  const [isFullScreen, setIsFullScreen] = useState(false)
  const isGuest = useSelector(selectClientIsGuest)
  const isImpersonating = hasCustomerToken()
  const favoriteListingsRoute = useSelector(selectRouteToFavoriteListings)
  const savedSearchesRoute = useSelector(selectRouteToSavedSearches)
  const { isLargeOrAbove } = useViewport()
  const [googleMapsInternals, setGoogleMapsInternals] = useState<any>()
  const mapRef = useRef<HTMLDivElement>(null)
  const mapRefContainer = useRef(null)
  const [loadedListings, setLoadedListings] = useState(false)
  const [mapState, setMapState] = useRecoilState(homeSearchMapState)
  const { theme } = useTheme()
  const selectedListingId = useSelector(selectSelectedListingId)
  const [searchLocation, setSearchLocation] = useRecoilState(currentSearchLocation)
  const googleApiKey = { key: process.env.REACT_APP_GOOGLE_KEY }
  const { filters, page } = useFilters()
  const { data: listings } = useGetListingsForArea(
    {
      ...filters,
      page
    },
    {
      enabled:
        !!filters?.searchArea &&
        (!!filters?.searchArea?.boundingBox || !!filters?.searchArea?.placeIdInput || !!filters?.searchArea?.geoArea)
    }
  )
  const { trackingClient } = useTrackingContext()
  const [trackMapPanned] = useTrackingDebounceSetters(() => {
    trackingClient?.mapModified({
      search_map_adjustment: TrackingEventConstants.MAP_PANNED
    })
  })
  const [trackMapZoomedIn] = useTrackingDebounceSetters(() => {
    trackingClient?.mapModified({
      search_map_adjustment: TrackingEventConstants.MAP_ZOOM_IN
    })
  })
  const [trackMapZoomedOut] = useTrackingDebounceSetters(() => {
    trackingClient?.mapModified({
      search_map_adjustment: TrackingEventConstants.MAP_ZOOM_OUT
    })
  })

  useEffect(() => {
    setLoadedListings(!isDelayLoading)
  }, [isDelayLoading])

  useEffect(() => {
    googleMapsInternals?.map?.setCenter({
      lat: location?.latitude ? location?.latitude : filter?.location?.lat,
      lng: location?.longitude ? location?.longitude : filter?.location?.lon
    })
  }, [
    location?.name,
    filter.location?.name,
    filter.location?.id,
    filter.minPrice,
    filter.maxPrice,
    filter.minBathroomsCount,
    filter.minBedroomsCount,
    filter.minSqft,
    filter.maxSqft,
    filter.minYearBuilt,
    filter.maxYearBuilt
  ])

  const mapOptions = maps => ({
    mapId: theme.isLightThemeType ? REACT_APP_GOOGLE_MAPS_ID_LIGHT : REACT_APP_GOOGLE_MAPS_ID_DARK,
    clickableIcons: true,
    mapTypeControl: true,
    minZoom: MIN_MAP_ZOOM,
    maxZoom: MAX_MAP_ZOOM,
    zoomControlOptions: {
      position: maps.ControlPosition.RIGHT_TOP
    },
    scrollwheel: true,
    disableDefaultUI: false,
    streetViewControl: true,
    streetViewControlOptions: {
      position: maps.ControlPosition.RIGHT_TOP
    },
    scaleControl: true,
    gestureHandling: 'greedy',
    zoomControl: true
  })

  const queryParams = camelizeKeys(
    qs.parse(window.location.search, {
      ignoreQueryPrefix: true
    })
  )

  const handleChange = state => {
    const boundingBox = {
      top_left: {
        lat: state.bounds?.nw?.lat,
        lon: state.bounds?.nw?.lng
      },
      bottom_right: {
        lat: state.bounds?.se?.lat,
        lon: state.bounds?.se?.lng
      }
    }

    if (state?.center.lat !== mapState?.center.lat || state?.center.lng !== mapState?.center.lng) {
      dispatch(setPreviouslyViewedListingId(''))
      trackMapPanned()
    }

    if (mapState?.zoom && state.zoom > mapState.zoom) {
      dispatch(setPreviouslyViewedListingId(''))
      trackMapZoomedIn()
    }

    if (mapState?.zoom && state.zoom < mapState.zoom) {
      dispatch(setPreviouslyViewedListingId(''))
      trackMapZoomedOut()
    }

    dispatch(setSelectedListingId(''))

    const element = document?.getElementById('list-top')

    element?.scrollIntoView({
      behavior: scrollBehavior,
      block: 'center'
    })

    dispatch(setFilter({ boundingBox }))

    setMapState({
      displayName: location?.name,
      bounds: state.bounds,
      center: {
        lat: state?.center?.lat,
        lng: state?.center?.lng
      },
      zoom: state.zoom <= 7 ? 7 : state.zoom
    })

    setIsMobileViewExpanded(false)
  }

  useEffect(() => {
    if (
      ((!mapState?.center.lat || !mapState?.center.lat || !mapState?.zoom) && location?.name) ||
      (location?.name !== mapState?.displayName && location?.name)
    ) {
      setMapState({
        displayName: location?.name,
        center: {
          lat: location?.latitude,
          lng: location?.longitude
        },
        zoom: location?.level === GeoAreaLevel.State ? MIN_MAP_ZOOM : STARTING_ZOOM
      })
    } else if (
      !location &&
      !mapState?.center.lat &&
      !mapState?.center.lng &&
      !mapState?.zoom &&
      filter?.location?.name
    ) {
      setMapState({
        displayName: filter?.location?.name,
        center: {
          lat: filter?.location?.lat,
          lng: filter?.location?.lon
        },
        zoom: location?.level === GeoAreaLevel.State ? MIN_MAP_ZOOM : STARTING_ZOOM
      })
      // if coming in from saved search that was created with bounding box
    } else if (
      !location &&
      !mapState?.center.lat &&
      !mapState?.center.lng &&
      !mapState?.zoom &&
      queryParams.topLeftLat &&
      queryParams.topLeftLong &&
      queryParams.bottomRightLat &&
      queryParams.bottomRightLong
    ) {
      const boundsFromQueryParams = {
        nw: {
          lat: queryParams.topLeftLat,
          lng: queryParams.topLeftLong
        },
        se: {
          lat: queryParams.bottomRightLat,
          lng: queryParams.bottomRightLong
        },
        sw: {
          lat: queryParams.bottomRightLat,
          lng: queryParams.topLeftLong
        },
        ne: {
          lat: queryParams.topLeftLat,
          lng: queryParams.bottomRightLong
        }
      }

      setSearchLocation(undefined)

      const calculatedBounds = fitBounds(boundsFromQueryParams, {
        width: mapRef?.current?.offsetWidth ?? 0,
        height: mapRef?.current?.offsetHeight ?? 0
      })

      setMapState({
        displayName: 'Bounding Box Saved Search',
        center: calculatedBounds.center,
        zoom: calculatedBounds.zoom
      })
    }
  }, [
    filter,
    searchLocation,
    mapState,
    queryParams.topLeftLat,
    queryParams.topLeftLong,
    queryParams.bottomRightLat,
    queryParams.bottomRightLong
  ])

  const handleSignupSigninClick = () => {
    dispatch(toggleAuthModal(true))
  }

  document.addEventListener('fullscreenchange', event => {
    setIsFullScreen(!isFullScreen)
  })
  document.addEventListener('webkitfullscreenchange', event => {
    setIsFullScreen(!isFullScreen)
  })
  document.addEventListener('mozfullscreenchange', event => {
    setIsFullScreen(!isFullScreen)
  })
  document.addEventListener('MSFullscreenChange', event => {
    setIsFullScreen(!isFullScreen)
  })

  useEffect(() => {
    const localityFeatureLayer = googleMapsInternals?.map?.getFeatureLayer('LOCALITY')
    const postalCodeFeatureLayer = googleMapsInternals?.map?.getFeatureLayer('POSTAL_CODE')
    const stateFeatureLayer = googleMapsInternals?.map?.getFeatureLayer('ADMINISTRATIVE_AREA_LEVEL_1')
    const featureLayer =
      location?.level === GeoAreaLevel.City
        ? localityFeatureLayer
        : location?.level === GeoAreaLevel.PostalCode
        ? postalCodeFeatureLayer
        : stateFeatureLayer

    const styleFill = {
      strokeColor: '#438ED9',
      strokeOpacity: 1.0,
      strokeWeight: 3.0,
      fillColor: '#99CCF8',
      fillOpacity: 0.2
    }

    if (featureLayer) {
      // clear out all previous boundaries before drawing new one
      localityFeatureLayer.style = params => {
        return null
      }
      postalCodeFeatureLayer.style = params => {
        return null
      }
      stateFeatureLayer.style = params => {
        return null
      }
      // draw the new boundary
      featureLayer.style = params => {
        if (
          params.feature.placeId == location?.placeId &&
          // when we come from saved search, we don't set search location
          (searchLocation || queryParams.from === 'saved_search_place_id')
        ) {
          return styleFill
        }

        return null
      }
    }
  }, [googleMapsInternals, location?.level, location?.placeId, searchLocation, mapState])

  return (
    <>
      {(isDelayLoading || !loadedListings) && (
        <div className={styles.loading}>
          <MagnifyingGlassLoader />
        </div>
      )}
      <div id='map-web-container' ref={mapRefContainer} className={styles.mapContainer}>
        {isLargeOrAbove &&
          (isGuest && !isImpersonating ? (
            <Flex justifyContent='flex-end'>
              <ButtonGroup h='3rem' alignItems='center' paddingRight='3'>
                <Button
                  onClick={handleSignupSigninClick}
                  size='sm'
                  leftIcon={<Icon name='heart' />}
                  variant='ghost'
                  tracking={{
                    guid: '8HQcYRRAsfZnBa4uM5YbDs',
                    ui_context: HomeSearchTrackingEventLocation.HOME_SEARCH_FAVORITES_BUTTON,
                    descriptor: MSG.favorites.defaultMessage
                  }}
                >
                  {intl.formatMessage(MSG.favorites)}
                </Button>
                <Button
                  onClick={handleSignupSigninClick}
                  size='sm'
                  leftIcon={<SavedSearchIcon />}
                  variant='ghost'
                  tracking={{
                    guid: '3E1YGyVFW9VhbgqpvvZ57P',
                    ui_context: HomeSearchTrackingEventLocation.HOME_SEARCH_SAVED_SEARCHES_BUTTON,
                    descriptor: MSG.searches.defaultMessage
                  }}
                >
                  {intl.formatMessage(MSG.searches)}
                </Button>
              </ButtonGroup>
            </Flex>
          ) : (
            <Flex justifyContent='flex-end'>
              <ButtonGroup h='3rem' alignItems='center' paddingRight='3'>
                <LinkButton
                  as={ReactRouterLink}
                  to={favoriteListingsRoute}
                  size='sm'
                  variant='ghost'
                  leftIcon={<Icon name='heart' />}
                  tracking={{
                    guid: 'fYwWDhSA6gVHwghRUBptN7',
                    ui_context: HomeSearchTrackingEventLocation.HOME_SEARCH_FAVORITES_BUTTON,
                    descriptor: MSG.favorites.defaultMessage
                  }}
                >
                  {intl.formatMessage(MSG.favorites)}
                </LinkButton>
                <LinkButton
                  as={ReactRouterLink}
                  to={savedSearchesRoute}
                  size='sm'
                  variant='ghost'
                  leftIcon={<SavedSearchIcon />}
                  tracking={{
                    guid: 'oU9txpMyjQXzh8KGHoUBf4',
                    ui_context: HomeSearchTrackingEventLocation.HOME_SEARCH_SAVED_SEARCHES_BUTTON,
                    descriptor: MSG.searches.defaultMessage
                  }}
                >
                  {intl.formatMessage(MSG.searches)}
                </LinkButton>
              </ButtonGroup>
            </Flex>
          ))}
        <div id='map-wrapper' ref={mapRef} className={styles.map}>
          {!isDelayLoading && location?.placeId && (
            <Button
              size='sm'
              left='50%'
              zIndex={1}
              position='absolute'
              colorScheme='primary'
              transform='translateX(-50%)'
              bottom={['30%', null, null, 28]}
              onClick={() => setSearchLocation(undefined)}
            >
              {intl.formatMessage(MSG.searchThisArea)}
            </Button>
          )}
          <GoogleMapReact
            options={mapOptions}
            zoom={mapState?.zoom}
            onChange={handleChange}
            center={mapState?.center as { lat: number; lng: number }}
            bootstrapURLKeys={googleApiKey as unknown as BootstrapURLKeys}
            yesIWantToUseGoogleMapApiInternals
            onDrag={() => {
              setLoadedListings(false)
            }}
            onGoogleApiLoaded={({ map, maps }) => {
              setGoogleMapsInternals({ map, maps })

              map?.addListener('dragstart', () => {
                setLoadedListings(false)
              })
              map?.addListener('dragend', () => {
                setTimeout(() => {
                  setLoadedListings(true)
                }, 200)
              })
            }}
          >
            {!isDelayLoading &&
              listings?.map(listing => {
                if (listing?.showMapLink === false || !loadedListings) return null

                return (
                  <Marker
                    isFullScreen={isFullScreen}
                    key={`marker-${listing.id}`}
                    // @ts-ignore lat/lng are not actual Marker props they are used by GoogleMapReact to correctly position the marker
                    lat={listing?.address?.latitude}
                    lng={listing?.address?.longitude}
                    selectedListingId={selectedListingId}
                    listing={listing}
                    onClick={onMarkerClick}
                  />
                )
              })}
          </GoogleMapReact>
        </div>
      </div>
    </>
  )
}
